import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Player, TeamInLeague, TacticalPosition, GameState, Position } from '../../domain/entities';
import { POSITION_MAP, FORMATIONS } from '../../domain/constants';
import { Modal } from '../shared/Modal';
import { PlayerFace } from '../shared/PlayerFace';
import { FitnessBadge } from '../shared/FitnessBadge';
import { MoraleBadge } from '../shared/MoraleBadge';
import { calculateMatchPerformance, calculatePositionalPenalty, calculateExpectedPerformance } from '../../utils/utilities';
import { MatchPerformanceDetailModal } from '../shared/MatchPerformanceDetailModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAmbulance, faBandage } from '@fortawesome/free-solid-svg-icons';
import './PlayerAssignmentModal.css';

interface PlayerAssignmentModalProps {
    isOpen: boolean;
    onClose: () => void;
    team: TeamInLeague;
    gameState: GameState;
    
    // Mode 1: Slot-first (from TacticsScreen)
    selectedSlot?: TacticalPosition;
    onAssignPlayer?: (player: Player) => void;
    onUnassign?: () => void;

    // Mode 2: Player-first (from PlayerDetailScreen)
    playerToAssign?: Player;
    onSwapPlayers?: (playerToReplaceId: number) => void;
    getPlayerById?: (id: number) => Player | undefined;
}

export const PlayerAssignmentModal: React.FC<PlayerAssignmentModalProps> = (props) => {
    const { 
        isOpen, 
        onClose, 
        team, 
        gameState,
        selectedSlot,
        onAssignPlayer,
        onUnassign,
        playerToAssign,
        onSwapPlayers,
        getPlayerById,
    } = props;

    // FIX: Destructure lineup and squad from team to be used throughout the component.
    const { squad, tactics } = team;
    const { lineup } = tactics;

    const isPlayerFirstMode = !!playerToAssign;
    const [searchText, setSearchText] = useState('');
    const [performanceModalData, setPerformanceModalData] = useState<{ player: Player, position?: TacticalPosition } | null>(null);

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [isCompact, setIsCompact] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (isOpen) {
            setSearchText('');
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            // On mobile, always use compact view; on desktop, keep user's preference
            if (mobile) {
                setIsCompact(true);
            }
        }
    }, [isOpen]);

    // --- PLAYER-FIRST MODE (Replaces AssignToLineupModal) ---
    if (isPlayerFirstMode) {
        if (!playerToAssign || !getPlayerById || !onSwapPlayers) return null;

        const requiredPositionForAssignment = playerToAssign.position;
        const formationPositions = FORMATIONS[team.tactics.formation] || [];
        const startingXI = formationPositions
            .filter(pos => {
                // Only show positions that match the player's position
                const positionFromPos = POSITION_MAP[pos as TacticalPosition];
                return positionFromPos === requiredPositionForAssignment;
            })
            .map(pos => {
                const playerId = lineup[pos];
                const player = playerId ? getPlayerById(playerId) : null;

                // Calculate expected performance (no penalty since position matches)
                const currentPlayerPerf = player ? calculateExpectedPerformance(player, pos as TacticalPosition) : 0;
                const playerToAssignEffectivePerf = calculateExpectedPerformance(playerToAssign, pos as TacticalPosition);

                return {
                    pos,
                    player,
                    currentPlayerPerf,
                    playerToAssignEffectivePerf,
                };
            })
            .filter(item => item.player && item.player.id !== playerToAssign.id);

        const isAssigning = !Object.values(lineup).includes(playerToAssign.id);
        const modalTitle = isAssigning ? 'Assign Player to XI' : 'Change Position';
        const modalDescription = isAssigning
            ? `Select a player from the lineup to replace. ${playerToAssign.name} will take their position.`
            : `Select a player from the lineup to swap positions with ${playerToAssign.name}.`;

        const handlePerformanceClick = (e: React.MouseEvent, player: Player, position: TacticalPosition) => {
            e.stopPropagation();
            setPerformanceModalData({ player, position });
        };

        return (
            <Modal isOpen={isOpen} onClose={onClose}>
                <h2>{modalTitle}</h2>
                <p>{modalDescription}</p>
                <div className="table-wrapper" style={{ maxHeight: '60vh', overflowY: 'auto', marginTop: '16px' }}>
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Position</th>
                                <th>Player</th>
                                <th>Match Perf.</th>
                                <th style={{ whiteSpace: 'normal', textAlign: 'center' }}>
                                    <div>{playerToAssign.name}</div>
                                    <div style={{ fontSize: '0.9em', fontWeight: 'normal' }}>Match Perf.</div>
                                </th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {startingXI.map(({ pos, player, currentPlayerPerf, playerToAssignEffectivePerf }) => (
                                <tr key={pos}>
                                    <td>{pos.startsWith('ST (') ? 'ST' : pos}</td>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            {player?.injury && <FontAwesomeIcon icon={faAmbulance} className="negative" title={`${player.injury.type} Injury (${player.injury.duration} wks)`} />}
                                            {player?.name}
                                        </div>
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                        <button
                                            className="link-style"
                                            onClick={(e) => player && handlePerformanceClick(e, player, pos as TacticalPosition)}
                                            title="Click to see performance breakdown"
                                        >
                                            {currentPlayerPerf}
                                        </button>
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                        <button
                                            className="link-style"
                                            onClick={(e) => handlePerformanceClick(e, playerToAssign, pos as TacticalPosition)}
                                            title="Click to see performance breakdown"
                                        >
                                            {playerToAssignEffectivePerf}
                                        </button>
                                    </td>
                                    <td>
                                        <button
                                            className="button"
                                            onClick={() => player && onSwapPlayers(player.id)}
                                            style={{ padding: '4px 8px', fontSize: '0.9em' }}
                                        >
                                            Assign
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="modal-actions">
                    <button className="button" onClick={onClose}>Cancel</button>
                </div>
                {performanceModalData && (
                    <MatchPerformanceDetailModal
                        player={performanceModalData.player}
                        isOpen={!!performanceModalData}
                        onClose={() => setPerformanceModalData(null)}
                        tacticalPosition={performanceModalData.position}
                    />
                )}
            </Modal>
        );
    }

    // --- SLOT-FIRST MODE (Original PlayerAssignmentModal) ---
    if (!selectedSlot || !onAssignPlayer || !onUnassign) return null;

    const requiredPosition = POSITION_MAP[selectedSlot];

    const currentlyAssignedPlayer = useMemo(() => {
        const playerId = lineup[selectedSlot!];
        return squad.find(p => p.id === playerId) || null;
    }, [lineup, selectedSlot, squad]);

    const { availablePrimary, suggestedPlayerId } = useMemo(() => {
        if (!selectedSlot) return { availablePrimary: [], suggestedPlayerId: null };

        // Only show players that match the required position
        const availablePlayers = squad
            .filter(p => p.position === requiredPosition && p.id !== currentlyAssignedPlayer?.id && p.injury?.type !== 'Severe' && !p.suspension)
            .map(p => {
                const finalPerf = calculateExpectedPerformance(p, selectedSlot);
                return { ...p, finalPerf };
            })
            .sort((a, b) => b.finalPerf - a.finalPerf);

        const suggestedPlayer = availablePlayers.length > 0 ? availablePlayers[0] : null;

        return {
            availablePrimary: availablePlayers,
            suggestedPlayerId: suggestedPlayer?.id
        };
    }, [squad, currentlyAssignedPlayer, requiredPosition, selectedSlot]);

    const filterPlayers = useCallback(<T extends Player>(players: T[]) => {
        return players
            .filter(p => p.name.toLowerCase().includes(searchText.toLowerCase()));
    }, [searchText]);

    const filteredPrimary = useMemo(() => filterPlayers(availablePrimary), [availablePrimary, filterPlayers]);

    const PlayerRow: React.FC<{ player: Player, isSuggested?: boolean, isCurrent?: boolean }> = ({ player, isSuggested = false, isCurrent = false }) => {
        const finalPerf = calculateExpectedPerformance(player, selectedSlot);
        const formClass = player.form > 0 ? 'positive' : player.form < 0 ? 'negative' : 'text-secondary';

        const currentAssignment = Object.keys(lineup).find(pos => lineup[pos as TacticalPosition] === player.id);
        const statusTitle = currentAssignment ? `Currently in XI at ${currentAssignment}` : 'Reserve';
        
        const handlePerfClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            setPerformanceModalData({ player, position: selectedSlot });
        };
        
        if ((isMobile || isCompact)) {
            return (
                <div onClick={() => !isCurrent && onAssignPlayer(player)} className={`player-assignment-card ${isSuggested ? 'suggested-player' : ''} ${isCurrent ? '' : 'clickable'}`} title={isCurrent ? 'Currently assigned' : `Assign ${player.name} to ${selectedSlot}`}>
                    <div className="card-row-1">
                        <div className="player-info">
                            {isSuggested && <span className="suggestion-icon" title="Suggested">⭐</span>}
                            <PlayerFace player={player} teamColor={team.colors.primary} />
                            <div className="player-name-details">
                                <span className="player-name">{player.name}</span>
                                <small className="player-assignment-status">
                                    {isCurrent ? `Currently assigned` : statusTitle}
                                    {isSuggested && <span className="suggestion-text"> ⚡ Suggested</span>}
                                </small>
                            </div>
                        </div>
                            <div className="player-perf">
                                <label>Match Perf</label>
                                <button className="link-style" onClick={handlePerfClick} title="Click for perf breakdown">
                                    <strong>{Math.round(finalPerf)}</strong>
                                </button>
                            </div>

                    </div>
                    <div className="card-row-2">
                        <div className="player-position-info">
                            <div className="player-detail-item">
                                <label>POS</label>
                                <span>{player.position}</span>
                            </div>
                            <div className="player-detail-item">
                                <label>ROLE</label>
                                <span title={player.favoredPositionCategory}>{player.favoredPositionCategory}</span>
                            </div>
                            <div className="player-detail-item">
                                <label>FOOT</label>
                                <span title={player.favoredFoot}>{player.favoredFoot.charAt(0)}</span>
                            </div>
                        <div className="player-status-badges">
                            {player.injury && player.injury.type !== 'Severe' && (
                                <span className="injury-badge" title={`${player.injury.type} Injury (${player.injury.duration} wks)`}>
                                    <FontAwesomeIcon icon={faBandage} /> {player.injury.type}
                                </span>
                            )}
                            <FitnessBadge fitness={player.fitness} />
                            <MoraleBadge morale={player.morale} />
                        </div>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <tr onClick={() => !isCurrent && onAssignPlayer(player)} className={`${isSuggested ? 'suggested-player' : ''} ${isCurrent ? '' : 'clickable-row'}`} title={isCurrent ? 'Currently assigned' : `Assign ${player.name} to ${selectedSlot}`}>
                <td>
                    <div className="player-name-cell">
                        {isSuggested && <span className="suggestion-icon" title="Suggested">⭐</span>}
                        <PlayerFace player={player} teamColor={team.colors.primary} />
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {player.name}
                                {player.injury && player.injury.type !== 'Severe' && (
                                    <span className="injury-badge" style={{ fontSize: '0.8em', padding: '2px 6px' }} title={`${player.injury.type} Injury (${player.injury.duration} wks)`}>
                                        <FontAwesomeIcon icon={faBandage} /> {player.injury.type}
                                    </span>
                                )}
                            </div>
                            <small className="player-assignment-status">
                                {isCurrent ? `Currently assigned to ${selectedSlot}` : statusTitle}
                            </small>
                            {isSuggested && <small className="suggestion-text">⚡ Suggested - Best available option</small>}
                        </div>
                    </div>
                </td>
                <td>{player.age}</td>
                <td>{player.position}</td>
                <td title={player.favoredPositionCategory}>{player.favoredPositionCategory}</td>
                <td title={player.favoredFoot}>{player.favoredFoot.charAt(0)}</td>
                <td>{player.skill}</td>
                <td className={formClass} style={{fontWeight: 'bold'}}>{player.form > 0 ? `+${player.form}`: player.form}</td>
                <td>
                    <button className="link-style" onClick={handlePerfClick} style={{ fontWeight: 'bold', display: 'block', width: '100%', textAlign: 'center' }} title="Click to see performance breakdown">
                        {Math.round(finalPerf)}
                    </button>
                </td>
                <td><FitnessBadge fitness={player.fitness} /></td>
                <td><MoraleBadge morale={player.morale} /></td>
            </tr>
        );
    };

    const fullTableHeaders = (
        <tr>
            <th>NAME</th>
            <th>AGE</th>
            <th>POS</th>
            <th>DETAIL</th>
            <th>FOOT</th>
            <th>SKL</th>
            <th>FORM</th>
            <th>MATCH PERF.</th>
            <th>FIT</th>
            <th>MORALE</th>
        </tr>
    );
    
    const modalClassName = `player-assignment-modal ${isMobile && !isCompact ? 'mobile-full-view' : ''}`;

    return (
        <Modal isOpen={isOpen} onClose={onClose} className={modalClassName}>
            <div className="player-assignment-modal-header">
                <h3>ASSIGN PLAYER TO {selectedSlot} ({requiredPosition})</h3>
                {!isMobile && (
                    <button className="button small" onClick={() => setIsCompact(c => !c)}>
                        {isCompact ? 'Full View' : 'Compact View'}
                    </button>
                )}
            </div>

            {currentlyAssignedPlayer && (
                <div className="currently-assigned-player">
                    <div className="panel-header" style={{ color: 'var(--text-color)', fontSize: '0.9em' }}>CURRENTLY ASSIGNED:</div>
                    {(isMobile || isCompact) ? (
                        <div className="player-assignment-card-list" style={{padding: '8px', maxHeight: 'none'}}>
                           <PlayerRow player={currentlyAssignedPlayer} isCurrent={true} />
                        </div>
                    ) : (
                        <div className="table-wrapper">
                            <table className="table assignment-table">
                                <thead>{fullTableHeaders}</thead>
                                <tbody>
                                    <PlayerRow player={currentlyAssignedPlayer} isCurrent={true} />
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            <div className="player-assignment-filters">
                <input
                    type="text"
                    placeholder="SEARCH BY NAME..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                />
            </div>
            
            <div className="player-assignment-scroll-wrapper">
                <div className="available-players-section">
                    <div className="panel-header" style={{ color: 'var(--accent-text-color)', fontSize: '0.9em' }}>AVAILABLE {requiredPosition}S</div>
                    {(isMobile || isCompact) ? (
                         <div className="player-assignment-card-list">
                            {filteredPrimary.map(p => <PlayerRow key={p.id} player={p} isSuggested={p.id === suggestedPlayerId} />)}
                         </div>
                    ) : (
                        <div className="table-wrapper">
                            <table className="table assignment-table">
                                <thead>{fullTableHeaders}</thead>
                                <tbody>
                                    {filteredPrimary.map(p => <PlayerRow key={p.id} player={p} isSuggested={p.id === suggestedPlayerId} />)}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <div className="player-assignment-modal-footer">
                <button className="button danger" onClick={onUnassign} disabled={!currentlyAssignedPlayer}>UNASSIGN PLAYER</button>
                <button className="button secondary" onClick={onClose}>CANCEL</button>
            </div>
             {performanceModalData && (
                <MatchPerformanceDetailModal
                    isOpen={!!performanceModalData}
                    onClose={() => setPerformanceModalData(null)}
                    player={performanceModalData.player}
                    tacticalPosition={performanceModalData.position || selectedSlot}
                />
            )}
        </Modal>
    );
};
